<template>
	<div class="reference-selector">
		<DxLookup v-if="!useTagbox" :data-source="lookupData" value-expr="id" :display-expr="displayMoteName" :label="props.label" :label-mode="props.isRowEditor ? 'hidden' : 'floating'"
			:search-enabled="true" :show-data-before-search="true" :min-search-length="2" search-mode="contains" search-placeholder="Type a few characters to search..."
			:show-clear-button="true" :show-cancel-button="true" :drop-down-centered="false" :drop-down-options="{ width: '400px' }"
			:value="internalValues[0] ? internalValues[0].mote_id : undefined " @value-changed="handleValueChanged"
		/>
		<DxTagBox v-else :data-source="lookupData" value-expr="id" :display-expr="displayMoteName" :label="props.label" :label-mode="props.isRowEditor ? 'hidden' : 'floating'"
			:search-enabled="true"
			:show-selection-controls="useSelectionControls" :drop-down-options="{ width: '400px' }" :show-drop-down-button="true"
			:value="internalValues && internalValues[0] ? internalValues.map(v => v.mote_id) : undefined" @value-changed="handleValueChanged"
		/>
	</div>
</template>

<script setup>
/* eslint-disable */
// Component for selecting a reference to any mote. This is a dropdown that can search for any mote of a given type through a variety of means.
// It is used in editors of relationship fields, but also in places where we want to select a single mote from a list, e.g. add existing links.
import { ref, defineProps, defineEmits, watch, inject, computed, nextTick } from 'vue';
import { DxLookup } from 'devextreme-vue/lookup';
import { DxTagBox } from 'devextreme-vue/tag-box';
import DataSource from 'devextreme/data/data_source';
import { SchemaCache } from '@/schemas';

const $http = inject('$http');

const useTagbox = computed(() => {
	return props.relationshipDetails && props.relationshipDetails.expectManyOpposites && !props.forceSingle;
});
const useSelectionControls = ref(false);
const internalValues = ref([]);

// We always expect a relationship details object specifying the type(s) of mote we can select from, and whether we can select multiple.
const props = defineProps({
	existing: {
		type: Object
	},
	relationshipDetails: {
		type: Object,
		required: true,
	},
	label: {
		type: String,
		default: '',
	},
	isRowEditor: {
		type: Boolean,
		default: false,
	},
	forceSingle: {			// Note that if this is specified, we should ONLY be using this as a dumb input -- since this will ignore degree restrictions, etc.
		type: Boolean,
		default: false,
	},
});
// We emit a simple single updated event with our internal values whenever they change.
const emit = defineEmits(['updated']);

// When we are provided with a props update, we read in our existing values to hydrate our component. The passed in values are our field payload:
// An array of objects, of either:
// - Relationship objects (rel_id, mote_id, payload)
// - Expanded relationship objects (complete mote values from mote_id, with _expanded containing the relationship object used)
const ignoreUpdates = ref(false);
watch(props, () => {
	if (ignoreUpdates.value) {
		return;
	}

	// Always take a copy, just in case.
	console.log("Props updated = ", JSON.stringify(props.existing));
	if (props.existing && Array.isArray(props.existing)) {
		internalValues.value = [];
		for (const val of props.existing) {
			if (val['_expanded']) {
				internalValues.value.push({
					rel_id: val['_expanded'].rel_id,
					mote_id: val['_expanded'].mote_id,
				});
			} else {
				internalValues.value.push({
					rel_id: val.rel_id,
					mote_id: val.mote_id,
				});
			}
		}
		console.log("Internal values initalized = ", JSON.stringify(internalValues.value));
	} else {
		internalValues.value = [];
	}
	
	console.log("Internal values initalized = ", JSON.stringify(internalValues.value));
}, { immediate: true });

// Data source for the mote lookup.
const lookupData = new DataSource({
	loadMode: 'raw',
	key: 'id',
	load: async() => {
		await SchemaCache.waitForData();
		// TODO: Implement multiple types later on!
		let types = props.relationshipDetails.oppositeSchemas.map(s => s.type);
		if (types.length > 1) {
			console.error('Reference Selector: Multiple types not yet implemented! Falling back to first type only.');
			types = types.slice(0, 1);
		}
		let fetchRequest = await $http.get('/mote', { params: {
			type: types[0]
        }});
        // TODO: How do we deal with the difference between mote and relation here? Right now we just discard all relation payloads and use only the mote payloads.
		let result = fetchRequest.data.motes.map(({ payload, ...datum }) => ({
			...(payload || {}),
			...datum
		}));
		console.log("Fetched reference selector data", result);
		return result;
	}
});

const displayMoteName = (data) => {
	if (!data) return '';
	if (data.name) return data.name;
	if (data.title) return data.title;
	return '#' + data.id;
}

// A value change in our component will update our internal values, and emit them to the parent.
const handleValueChanged = (e) => {
	if (ignoreUpdates.value) {
		return;
	}

	// If we are using a simple lookup (single selection), all changes will always be new relationships or empty.
	if (!useTagbox.value) {
		internalValues.value = e.value ? [{ rel_id: 0, mote_id: e.value }] : [];
	} else {
		// If we are using a tagbox (multiple selection), we need to check if we have values with rel_id's already.
		// The input value is just an array of integer mote_ids.
		console.log("Tagbox value changed = ", e.value);
		let replacementValues = [];
		for (const mote_id of e.value) {
			const existingValue = internalValues.value.find(v => v.mote_id === mote_id);
			if (existingValue) {
				replacementValues.push(existingValue);
			} else {
				replacementValues.push({ rel_id: 0, mote_id: mote_id });
			}
		}
		internalValues.value = replacementValues;
	}
	
	console.log("Internal values updated = ", JSON.stringify(internalValues.value));
	ignoreUpdates.value = true;
	emit('updated', internalValues.value);
	nextTick(() => {
		ignoreUpdates.value = false;
	});
}

</script>

<style lang="scss">
</style>
